import { CSSObject } from '@emotion/react'
import * as Dialog from '@radix-ui/react-dialog'
import classNames from 'classnames'
import { type ComponentProps, type ElementRef, forwardRef, FC } from 'react'
import {
  ThemeProvider,
  UserEnvironmentProvider,
  useTheme,
} from '@opallabs/hooks'
import { type WithRequiredTheme } from '../../gem'
import { ModalDialogContextProvider } from '../ModalDialogContext'
import * as styles from './styles'

const Portal: FC<ComponentProps<typeof Dialog.Portal>> = ({
  children,
  ...props
}) => {
  const theme = useTheme()

  /**
   * Dialog.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <Dialog.Portal {...props}>
      <UserEnvironmentProvider>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </UserEnvironmentProvider>
    </Dialog.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← Modal'

const Overlay = forwardRef<
  ElementRef<typeof Dialog.Overlay>,
  ComponentProps<typeof Dialog.Overlay> &
    WithRequiredTheme & { scrollable?: boolean }
>(
  (
    { children, scrollable = false, theme, className, ...props },
    forwardedRef
  ) => (
    <Portal>
      <Dialog.Overlay
        {...props}
        className={classNames(styles.classes.overlay, className)}
        css={styles.Overlay(theme)}
        data-scrollable={scrollable}
        ref={forwardedRef}
      >
        {scrollable ? children : null}
      </Dialog.Overlay>
      {!scrollable ? children : null}
    </Portal>
  )
)
Overlay.displayName = 'Lapidary: Overlay ← Modal'

export type ContentProps = ComponentProps<typeof Dialog.Content> & {
  scrollable?: boolean
  overlayStyles?: CSSObject
}
export const Content = forwardRef<
  ElementRef<typeof Dialog.Content>,
  ContentProps
>(
  (
    { children, className, scrollable, overlayStyles, ...props },
    forwardedRef
  ) => {
    const theme = useTheme()

    return (
      <Overlay css={overlayStyles} scrollable={scrollable} theme={theme}>
        <ModalDialogContextProvider>
          <Dialog.Content
            {...props}
            className={classNames([styles.classes.content, className])}
            css={styles.modalWrapper}
            onInteractOutside={(e) => e.preventDefault()}
            ref={forwardedRef}
          >
            {children}
          </Dialog.Content>
        </ModalDialogContextProvider>
      </Overlay>
    )
  }
)
Content.displayName = 'Lapidary: Content ← Modal'
